import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import PaymentHistory from './PaymentHistory';
import { formatCurrency, getInvoiceStatusColor} from '../../../utils/helper';

const InvoiceDetails = ({ 
  open, 
  onClose, 
  invoice = null,
  showSuccessMessage = null 
}) => {

  if (!invoice) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PD': return <CheckCircleIcon />;
      case 'UP': return <PaymentIcon />;
      case 'PP': return <WarningIcon />;
      case 'OV': return <WarningIcon />;
      case 'RP': return <WarningIcon />;
      case 'CN': return <WarningIcon />;
      default: return <ReceiptIcon />;
    }
  };

  const isOverdue = () => {
    if (!invoice.due_date) return false;
    return new Date(invoice.due_date) < new Date();
  };

  const getDaysUntilDue = () => {
    if (!invoice.due_date) return null;
    const dueDate = new Date(invoice.due_date);
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilDue = getDaysUntilDue();

  // Mock invoice items data
  const invoiceItems = (invoice.particulars || []).map((item) => ({
    id: item.id,
    description: item.description,
    quantity: item.quantity,
    price: item.unit_price,
    total: item.total_amount
  }));

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <ReceiptIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Invoice Details
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {/* Invoice Header */}
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mr: 2 }}>
                      Invoice #{invoice.invoice_number}
                    </Typography>
                    <Chip
                      icon={getStatusIcon(invoice.status)}
                      label={invoice.status_verbose}
                      color={getInvoiceStatusColor(invoice.status)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {formatCurrency(invoice.taxable_invoice_value)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Created on {formatDate(invoice.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Avatar sx={{ 
                      bgcolor: invoice.status === 'PD' 
                        ? 'success.main' 
                        : isOverdue() 
                          ? 'error.main' 
                          : 'warning.main', 
                      mr: 2 
                    }}>
                      <CalendarIcon />
                    </Avatar>
                    <Box sx={{ textAlign: 'right' }}>
                      {invoice.status === 'PD' ? (
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                          Paid on {formatDate(invoice.updated_at)}
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: isOverdue() ? 'error.main' : 'warning.main' }}>
                            Due {formatDate(invoice.due_date)}
                          </Typography>
                          {daysUntilDue !== null && (
                            <Typography variant="body2" color={isOverdue() ? 'error.main' : 'text.secondary'}>
                              {isOverdue() 
                                ? `${Math.abs(daysUntilDue)} days overdue`
                                : daysUntilDue === 0 
                                  ? 'Due today'
                                  : `${daysUntilDue} days remaining`
                              }
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={3}>
            {/* Invoice Items */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Invoice Items ({invoiceItems.length})
                    </Typography>
                  </Box>
                  
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">QTY</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Total Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceItems.map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              <Typography variant="body1">
                                {item.description}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1">
                                {item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1">
                                {formatCurrency(item.price)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {formatCurrency(item.total)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        
                        {/* Subtotal Row */}
                        <TableRow>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Subtotal
                            </Typography>
                          </TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {formatCurrency(invoice.invoice_value)}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Discount Percentage Row */}
                        {parseFloat(invoice.discount_percentage) > 0 && (
                          <TableRow>
                            <TableCell>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Discount
                              </Typography>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right">
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {invoice.discount_percentage}%
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Discount Amount Row */}
                        {parseFloat(invoice.discount_amount) > 0 && (
                          <TableRow>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Discount Amount
                              </Typography>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right">
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                -{formatCurrency(invoice.discount_amount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Tax Rate Row */}
                        <TableRow>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Tax Rate
                            </Typography>
                          </TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              18.0%
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Tax Amount Row */}
                        <TableRow>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Tax Amount
                            </Typography>
                          </TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {formatCurrency(invoice.tax_amount)}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Final Total Row */}
                        <TableRow>
                          <TableCell>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                              Total
                            </Typography>
                          </TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                              {formatCurrency(invoice.taxable_invoice_value)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Payment History */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                      <PaymentIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Payment History ({invoice.payment_history?.length || 0})
                    </Typography>
                  </Box>
                  
                  <PaymentHistory 
                    showPaymentHistory={true}
                    invoiceId={invoice.invoice_number}
                    onClose={null}
                    payment_history={invoice.payment_history}
                    showSuccessMessage={showSuccessMessage}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          {/* <Button onClick={onClose} color="inherit">
            Close
          </Button> */}
          {/* <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            sx={{ borderRadius: 2 }}
            onClick={() => {
              const link = document.createElement('a');
              link.href = invoice.invoice_url;
              link.download = `${invoice.invoice_number}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download PDF
          </Button> */}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvoiceDetails; 